import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { EPlatformsNames, EStripeSubStatus, ETrialStatus } from '../../../models/consts';
import { EGitlabPlanNames } from '../../../models/paymentPlans/gitlab-payments-plan';
import { ENotionPlanNames } from '../../../models/paymentPlans/notion-payments-plan';
import { IPlatformSubscriptionRow } from '../../../views/UserPages/Subscription/Subscription';
import { PlatformsState } from '../../reducers/platform';

export const getShopifyEntitySelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.items);

export const getShopifyCanUserBackup = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.canUserBackup);

export const isShopifyEntityLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.itemsLoading);

export const isShopifyUsersLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.usersLoading);

export const getShopifyUsersSelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.users
});
export const getShopifyAppInstallSelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.users?.[0]?.isAppInstalled
});
export const getShopifyConfirmedSelector = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.users?.[0]?.isConfirmed
});
export const isShopifyConnect = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.isConnectionActive);

export const shopifyTrialStatus = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.trial?.trialStatus);

export const getShopifyTotal = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => shopify.totalItems);

export const shopifyTrialHoursLeft = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.trial?.trialHoursLeft || 0;
});

export const shopifyTrialQuantityLeft = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.trial?.availableItemsQuantity || 0;
});

export const shopifyTrialPlanQuantity = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.trial?.planQuantity || 0;
});

export const shopifySubInfo = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { subscription } = shopify;
  if (subscription) {
    return {
      id: 6,
      platformName: EPlatformsNames.SHOPIFY,
      plan: subscription?.planName || '',
      price: `$${subscription?.isAnnual 
        ? (subscription.cost * 12).toFixed(2) + ' / yr.' : subscription.cost.toFixed(2) + ' / mo.'}`,
      status: subscription?.status,
      quantity: subscription.quantity,
      planQuantity: subscription.planQuantity,
      addedItemsQuantity: subscription.addedItemsQuantity,
      isCancel: subscription.isCancel,
      isConnect: false
    };
  }
  return null;
});

export const shopifyTrialInfo = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { trial } = shopify;
  if (trial) {
    return {
      id: 6,
      platformName: EPlatformsNames.SHOPIFY,
      plan: ENotionPlanNames.TRIAL,
      price: `-`,
      status: trial.trialStatus,
      quantity: 0,
      planQuantity: 0,
      addedItemsQuantity: 0,
      isCancel: false,
      isConnect: false
    };
  }
  return null;
});

export const shopifySubscription = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.subscription
});

export const shopifyTrialData = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  return shopify.trial
});

export const shopifyActualPlan = createSelector((state: DefaultRootState) => state.platform, ({ shopify }: PlatformsState) => {
  const { subscription: shopifySub, trial: shopifyTrial, isConnectionActive } = shopify;

  const shopifyItem: IPlatformSubscriptionRow = {
    id: 6,
    platformName: EPlatformsNames.SHOPIFY,
    plan: '-',
    price: '-',
    quantity: 0,
    planQuantity: 0,
    addedItemsQuantity: isConnectionActive ? 1 : 0,
    status: EStripeSubStatus.INACTIVE,
    isCancel: false,
    finishDateInMillisecond: null,
    isConnect: isConnectionActive
  };

  if (shopifyTrial && shopifyTrial.trialStatus === ETrialStatus.NOT_STARTED) {
    shopifyItem.plan = EGitlabPlanNames.TRIAL;
    shopifyItem.status = shopifyTrial.trialStatus;
    shopifyItem.quantity = shopifyTrial.availableItemsQuantity || 0;
    shopifyItem.addedItemsQuantity = isConnectionActive ? 1 : 0; //shopifyTrial.addedItemsQuantity || 0;
    shopifyItem.planQuantity = shopifyTrial.planQuantity || 0;
    shopifyItem.finishDate = shopifyTrial.finishDate || null;
    shopifyItem.isConnect = isConnectionActive
  }

  if (shopifyTrial && shopifyTrial.trialStatus === ETrialStatus.STARTED) {
    shopifyItem.plan = EGitlabPlanNames.TRIAL;
    shopifyItem.status = shopifyTrial.trialStatus;
    shopifyItem.quantity = shopifyTrial.availableItemsQuantity || 0;
    shopifyItem.addedItemsQuantity = isConnectionActive ? 1 : 0; //shopifyTrial.addedItemsQuantity || 0;
    shopifyItem.planQuantity = shopifyTrial.planQuantity || 0;
    shopifyItem.finishDate = shopifyTrial.finishDate || null;
    shopifyItem.isConnect = isConnectionActive
  }

  if (shopifySub && (!shopifyTrial || shopifyTrial?.trialStatus !== ETrialStatus.STARTED || shopifySub?.status === EStripeSubStatus.ACTIVE)) {
    if (shopifyTrial && shopifyTrial.trialStatus === ETrialStatus.STARTED && shopifySub.subscription_type && shopifySub.planName) {
      shopifyItem.plan = `${shopifySub.planName} (trial)`;
    } else {
      shopifyItem.plan = shopifySub.planName || '';
    }
    shopifyItem.status = shopifySub.status;
    shopifyItem.finishDate = shopifySub.finishDate || null;
    shopifyItem.quantity = shopifySub.quantity || 0;
    shopifyItem.addedItemsQuantity = isConnectionActive ? 1 : 0; //shopifySub.addedItemsQuantity || 0;
    shopifyItem.planQuantity = shopifySub.planQuantity || 0;
    shopifyItem.price = `$${shopifySub.cost.toFixed(2)} / ${shopifySub.isAnnual ? 'yr.' : 'mo.'}`
    shopifyItem.isCancel = shopifySub.isCancel;
    shopifyItem.finishDateInMillisecond = shopifySub.finishDateInMillisecond;
    shopifyItem.isConnect = isConnectionActive
  }

  return shopifyItem
});
